import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  RefObject,
} from 'react';

import * as Sentry from '@sentry/nextjs';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useTheme } from '@techstyle/react-components';

import generateCdnImageUrl from '../../../src/utils/generateCdnImageUrl';
import { trackViewedPdpImage } from '../../actions/products';
import { prepModelData } from '../../segment-events/utils';
import { mobile, desktop } from '../../styles';
import { Model as ModelTypes } from '../../types/modelTypes';
import { Product as ProductTypes } from '../../types/productTypes';
import fetchVideoThumbnail from '../../utils/fetchVideoThumbnail';
import formatVimeoThumbnailUrl from '../../utils/formatVimeoThumbnailUrl';
import usePrevious from '../../utils/usePrevious';
import { Component as ProductImageCarouselFullScreen } from '../ProductImageCarouselFullScreen';
import { VideoCoverSizes } from '../ProductVideo/ProductVideo';
import ThumbnailCarousel from '../ThumbnailCarousel/ThumbnailCarousel';

const Container = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;

  ${mobile`padding: 0px;`};
  ${desktop`
    padding: ${props => props.theme.spacing.small}px 0px;
  `}
`;

type ProductImageProps = {
  className: string;
  imageCarouselRef: RefObject<any>;
  isAmbassadorModelSelected: boolean;
  onAmbassadorModelSelect: (isAmbassadorModelChosen: boolean) => void;
  options: { onSwipe: Function };
  model: ModelTypes;
  /* eslint-disable camelcase */
  product: ProductTypes;
  shouldShowModelThumbnails: boolean;
  isCurvy?: boolean;
  isProductSet: boolean;
  handleModelImageFullScreen: () => void;
  isModelImageFullScreen: boolean;
  trackViewedPdpImageWithSessionStorage: (
    imageUrl: string,
    callback: () => void
  ) => void;
};

const ProductImageCarousel = ({
  className,
  imageCarouselRef,
  isAmbassadorModelSelected,
  onAmbassadorModelSelect,
  options,
  model,
  product,
  shouldShowModelThumbnails,
  isCurvy = false,
  isProductSet,
  handleModelImageFullScreen,
  isModelImageFullScreen,
  trackViewedPdpImageWithSessionStorage,
}: ProductImageProps) => {
  const dispatch = useDispatch();
  const theme = useTheme(null, null);
  const [activeSlide, setActiveSlide] = useState(0);
  const thumbnailSliderRef = useRef<{
    slickGoTo: (index: string | number, dontAnimate: boolean) => void;
  }>();
  const previousModel = usePrevious(model);
  const isPreviousAmbassadorModelSelected = usePrevious(
    isAmbassadorModelSelected
  );

  const getUrlFromVideoId = useCallback(
    (videoId: string, defaultService: string = 'vimeo') => {
      const regexp = new RegExp('https?://');

      if (regexp.test(videoId)) {
        return videoId;
      }

      const [serviceName, id] = `${videoId}`.includes(':')
        ? videoId.split(':')
        : [defaultService, videoId];

      if (serviceName === 'vimeo') {
        return `https://vimeo.com/${id}`;
      } else if (serviceName === 'youtube') {
        return `https://www.youtube.com/watch?v=${id}`;
      }
    },
    []
  );

  const trackViewedImage = useCallback(
    async (index, event) => {
      let angle = index + 1;
      let imageUrl;

      if (
        model.angles[index] === 'laydown' ||
        model.angles[index] === 'video'
      ) {
        angle = model.angles[index];
      }

      const { vimeo_id: vimeoId } = model.product;

      if (angle === 'video' && vimeoId) {
        try {
          const videoUrl = getUrlFromVideoId(vimeoId);

          const data = await fetchVideoThumbnail(videoUrl);

          if (data.thumbnail_url) {
            imageUrl = formatVimeoThumbnailUrl(
              data.thumbnail_url,
              VideoCoverSizes.SMALL
            );
          }
        } catch (err) {
          Sentry.captureException(err);
        }
      } else {
        imageUrl = generateCdnImageUrl({
          product,
          angle,
        });
      }

      const {
        master_product_id: _masterProductId = -1,
        product_id_object_list: productIdObjectList,
      } = product;

      const masterProductId = _masterProductId.toString();

      trackViewedPdpImageWithSessionStorage(imageUrl, () => {
        const modelDataInfo = prepModelData(model);
        const productDetail = productIdObjectList?.find(
          item => item.size === modelDataInfo.model_size
        );
        dispatch(
          trackViewedPdpImage({
            position: index + 1,
            location: 'carousel',
            bundle_product_id_hit: isProductSet ? masterProductId : null,
            label: event,
            image_url: imageUrl,
            is_plus_size: productDetail?.is_plus_size || isCurvy,
            product_id_hit: masterProductId,
            ...modelDataInfo,
          })
        );
      });
    },
    [
      dispatch,
      getUrlFromVideoId,
      trackViewedPdpImageWithSessionStorage,
      isCurvy,
      isProductSet,
      model,
      product,
    ]
  );

  useEffect(() => {
    const trackOnLoad = async () => {
      await trackViewedImage(0, 'load');
    };

    const timerId = setTimeout(() => trackOnLoad(), 500);

    return () => clearTimeout(timerId);
  }, [trackViewedImage]);

  const goToSlide = useCallback(
    (index, event) => {
      if (event) {
        trackViewedImage(index, event);
      }

      imageCarouselRef.current &&
        imageCarouselRef.current.slickGoTo(index, true);
    },
    [imageCarouselRef, trackViewedImage]
  );

  useEffect(() => {
    const isModelDifferent = model !== previousModel;
    const isSelectingInCombinedModel =
      isAmbassadorModelSelected !== isPreviousAmbassadorModelSelected;

    if (isModelDifferent) {
      // If the defined angle is unavailable, set the default to index 0
      const defaultAngleIndex =
        model.angles.findIndex(angle => angle === model.defaultAngle) || 0;

      if (!isAmbassadorModelSelected && model.isCombinedModel) {
        goToSlide(1, null);
      } else {
        goToSlide(defaultAngleIndex, null);

        // this fix a bug where the thumbnails slider would be offset in some situations
        // eslint-disable-next-line no-unused-expressions
        thumbnailSliderRef.current?.slickGoTo(defaultAngleIndex, true);

        // manually set activeSlide back to 0 here
        // because when we switch models/carousle images sets, and goToSlide(0), react-slick beforeChange is not being called
        // but beforeChange() will be called if we go to anyother slide other than 0
        setActiveSlide(defaultAngleIndex);
      }
    } else if (isSelectingInCombinedModel) {
      if (isAmbassadorModelSelected) {
        goToSlide(0, null);
      } else {
        goToSlide(activeSlide || 1, null);
      }
    }
  }, [
    activeSlide,
    goToSlide,
    isAmbassadorModelSelected,
    isPreviousAmbassadorModelSelected,
    model,
    previousModel,
  ]);

  const handleBeforeImageCarouselChange = useCallback(
    (oldIndex?: number, newIndex?: number): void => {
      // moving setState for activeSlide from goToSlide() to here
      // because goToSlide() only handles desktop situation when we hover or click on the thumbnail carousel
      // moving it to beforeChange will update activeSlide whenever slide changes for all scenarios we need
      setActiveSlide(newIndex);

      if (model.isCombinedModel && shouldShowModelThumbnails) {
        const isAmbassadorModelChosen = oldIndex !== 0 && newIndex === 0;
        onAmbassadorModelSelect(isAmbassadorModelChosen);
      }
    },
    [model.isCombinedModel, onAmbassadorModelSelect, shouldShowModelThumbnails]
  );

  return (
    <Container className={className} data-autotag="pdpItemImg">
      <ThumbnailCarousel
        model={model}
        thumbnailSliderRef={thumbnailSliderRef}
        goToSlide={goToSlide}
        activeSlide={activeSlide}
      />
      <ProductImageCarouselFullScreen
        handleBeforeImageCarouselChange={handleBeforeImageCarouselChange}
        modelAngles={model.angles}
        forwardedRef={imageCarouselRef}
        handleModelImageFullScreen={handleModelImageFullScreen}
        isModelImageFullScreen={isModelImageFullScreen}
        model={model}
        options={options}
        theme={theme}
      />
    </Container>
  );
};

export default ProductImageCarousel;
